// Sino de notificações no header (ver Layout.jsx). Não é tempo real — o
// contador é atualizado por polling leve (react-query refetchInterval) e ao
// reabrir a aba, o que é suficiente pra "central de notificações" pedida (ver
// Notificacoes.js pra a página cheia, com histórico e o form de aviso de
// sistema do master).
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Megaphone, UserPlus, ArrowRightLeft, UserMinus, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useInstitution } from '../contexts/InstitutionContext';
import NotificacaoDetalheModal from './NotificacaoDetalheModal';
import { API_URL } from '../api/config';
import { useApi } from '../api/useApi';
import { subscreverPush } from '../utils/pushNotification';

const ICONE_TIPO = {
  sistema: {
    Icon: Megaphone,
    cor: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30',
  },
  matricula: {
    Icon: UserPlus,
    cor: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30',
  },
  movimentacao: {
    Icon: ArrowRightLeft,
    cor: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30',
  },
  desistencia: {
    Icon: UserMinus,
    cor: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30',
  },
  chamada_pendente: {
    Icon: AlertTriangle,
    cor: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30',
  },
};

const tempoRelativo = (dataIso) => {
  const diffMs = Date.now() - new Date(dataIso).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min}min`;
  const horas = Math.floor(min / 60);
  if (horas < 24) return `${horas}h`;
  const dias = Math.floor(horas / 24);
  return `${dias}d`;
};

// `size="sm"` é usado no header compacto de Ajuste Grade (ver Layout.jsx) —
// mesmo componente, só menor, pra caber numa barra mais baixa sem cortar o
// botão nem o badge.
function NotificationBell({ size = 'md' }) {
  const { institutionId } = useInstitution();
  const { token } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [detalheAberto, setDetalheAberto] = useState(null);
  const [ativandoPush, setAtivandoPush] = useState(false);
  const [pushCadastrado, setPushCadastrado] = useState(false);
  const containerRef = useRef(null);

  const handleAtivarPush = async () => {
    try {
      setAtivandoPush(true);
      await subscreverPush(token, institutionId);
      setPushCadastrado(true);
      alert('Notificações ativadas com sucesso neste aparelho!');
    } catch (err) {
      alert(err.message || 'Não foi possível ativar as notificações.');
    } finally {
      setAtivandoPush(false);
    }
  };

  // Verifica se o navegador já possui inscrição ativa ao carregar a página
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      Notification.permission === 'granted'
    ) {
      navigator.serviceWorker.ready
        .then((reg) => reg.pushManager.getSubscription())
        .then((sub) => {
          if (sub) setPushCadastrado(true);
        })
        .catch(() => {});
    }
  }, []);

  // Fecha ao clicar fora — não dá pra usar um overlay fixed cobrindo a tela
  // (o padrão usado em outros dropdowns/menus do app), porque o <header> tem
  // transform-gpu (ver Layout.jsx) e isso vira o containing block de
  // qualquer elemento fixed dentro dele: o overlay ficaria preso na faixa de
  // 64px do header em vez de cobrir a página. Listener no document não tem
  // esse problema.
  useEffect(() => {
    if (!aberto) return;
    const fecharSeForaDoMenu = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', fecharSeForaDoMenu);
    return () => document.removeEventListener('mousedown', fecharSeForaDoMenu);
  }, [aberto]);

  const authHeaders = { 'x-institution-id': institutionId, Authorization: `Bearer ${token}` };
  const api = useApi();

  const { data: contagem = { total: 0 } } = useQuery({
    queryKey: ['notificacoes-contagem', institutionId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/notificacoes/contagem-nao-lidas`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Erro ao carregar notificações');
      const body = await res.json();
      return body?.data ?? body;
    },
    enabled: !!institutionId && !!token,
    refetchInterval: 120000,
  });

  const { data: notificacoes = [] } = useQuery({
    queryKey: ['notificacoes-recentes', institutionId],
    queryFn: async () => {
      const res = await api.get(`/api/notificacoes`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: aberto && !!institutionId && !!token,
  });

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['notificacoes-contagem', institutionId] });
    queryClient.invalidateQueries({ queryKey: ['notificacoes-recentes', institutionId] });
  };

  const marcarLida = async (id) => {
    await fetch(`${API_URL}/api/notificacoes/${id}/marcar-lida`, {
      method: 'POST',
      headers: authHeaders,
    });
    invalidar();
  };

  const marcarTodasLidas = async () => {
    await fetch(`${API_URL}/api/notificacoes/marcar-todas-lidas`, {
      method: 'POST',
      headers: authHeaders,
    });
    invalidar();
  };

  // Clicar numa notificação de evento (matrícula, mudança de turma,
  // desistência, ajuste em lote) abre o detalhe da alteração — o "de/para"
  // estruturado (ver NotificacaoDetalheModal), não só o texto solto. Aviso de
  // sistema (`tipo === 'sistema'`) não representa uma alteração de dado, só
  // marca como lida.
  const temDetalhe = (n) => n.tipo !== 'sistema';

  const irParaNotificacao = (n) => {
    if (!n.lida) marcarLida(n.id);
    if (temDetalhe(n)) {
      setAberto(false);
      setDetalheAberto(n);
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setAberto((prev) => !prev)}
        className={`relative rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-300 transition-colors ${size === 'sm' ? 'w-9 h-9' : 'w-10 h-10'}`}
        aria-label="Notificações"
      >
        <Bell className={size === 'sm' ? 'w-5 h-5' : 'w-[22px] h-[22px]'} />
        {contagem.total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white dark:ring-gray-900">
            {contagem.total > 99 ? '99+' : contagem.total}
          </span>
        )}
      </button>

      {aberto && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-[28rem] overflow-y-auto bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-xl z-50">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 sticky top-0 bg-white dark:bg-gray-900">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notificações</h3>
            {contagem.total > 0 && (
              <button
                onClick={marcarTodasLidas}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          <div className="p-2.5 bg-gray-50/80 dark:bg-gray-800/40 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-2">
            <span className="text-xs text-gray-700 dark:text-gray-300">
              🔔 Notificações no aparelho
            </span>
            {pushCadastrado ? (
              <span className="text-xs text-green-600 dark:text-green-400 font-medium flex items-center gap-1">
                ✓ Ativadas
              </span>
            ) : (
              <button
                type="button"
                disabled={ativandoPush}
                onClick={handleAtivarPush}
                className="text-xs bg-blue-600 hover:bg-blue-700 text-white font-medium px-2.5 py-1 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
              >
                {ativandoPush ? 'Ativando...' : 'Ativar'}
              </button>
            )}
          </div>

          {notificacoes.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">
              Nenhuma notificação ainda.
            </p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {notificacoes.slice(0, 15).map((n) => {
                const { Icon, cor } = ICONE_TIPO[n.tipo] || ICONE_TIPO.sistema;
                return (
                  <button
                    key={n.id}
                    onClick={() => irParaNotificacao(n)}
                    title={temDetalhe(n) ? 'Ver detalhes da alteração' : undefined}
                    className={`w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors ${!n.lida ? 'bg-blue-50/40 dark:bg-blue-900/10' : ''}`}
                  >
                    <div
                      className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${cor}`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate"
                        translate="no"
                      >
                        {n.titulo}
                      </p>
                      {n.mensagem && (
                        <p
                          className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5"
                          translate="no"
                        >
                          {n.mensagem}
                        </p>
                      )}
                      <p className="text-[10px] text-gray-400 dark:text-gray-600 mt-1">
                        {tempoRelativo(n.created_at)}
                      </p>
                    </div>
                    {!n.lida && (
                      <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <button
            onClick={() => {
              setAberto(false);
              navigate('/notificacoes');
            }}
            className="w-full text-center text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800/60 py-2.5 border-t border-gray-100 dark:border-gray-800 transition-colors sticky bottom-0 bg-white dark:bg-gray-900"
          >
            Ver todas
          </button>
        </div>
      )}

      <NotificacaoDetalheModal notificacao={detalheAberto} onClose={() => setDetalheAberto(null)} />
    </div>
  );
}

export default NotificationBell;
